import JumboApp from "@jumbo/components/JumboApp";
import JumboAuthProvider from "@jumbo/components/JumboAuthProvider";
import JumboDialog from "@jumbo/components/JumboDialog";
import JumboDialogProvider from "@jumbo/components/JumboDialog/JumboDialogProvider";
import JumboTheme from "@jumbo/components/JumboTheme";
import JumboRTL from "@jumbo/JumboRTL/JumboRTL";
import Div from "@jumbo/shared/Div";
import { CircularProgress } from "@mui/material";
import { SnackbarProvider } from "notistack";
import { Suspense, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "react-query";
import { Provider, useSelector } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import store from "stores";
import AppLayout from "./AppLayout";
import AppProvider from "./AppProvider";
import AppRoutes from "./AppRoutes";
import { config } from "./config/main";
import { history } from './redux/store';

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
        },
    },
});

// This component will be rendered after Redux Provider is loaded
function AppContent() {
    const fontSettings = useSelector((state) => state.meeting.fontSettings);

    useEffect(() => {
        // Set CSS variables for global font settings
        if (fontSettings) {
            document.documentElement.style.setProperty('--base-font-size', fontSettings.baseFontSize);
            document.documentElement.style.setProperty('--avatar-size', fontSettings.avatarSize);
            document.documentElement.style.setProperty('--icon-size', `calc(${fontSettings.avatarSize} * 0.6)`);
        }
    }, [fontSettings]);

    return (
        <JumboApp activeLayout={config.defaultLayout}>
            <JumboTheme init={config.theme}>
                <JumboRTL>
                    <JumboDialogProvider>
                        <JumboDialog />
                        <SnackbarProvider
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            maxSnack={3}>
                            <AppLayout>
                                <Suspense
                                    fallback={
                                        <Div
                                            sx={{
                                                display: 'flex',
                                                minWidth: 0,
                                                alignItems: 'center',
                                                alignContent: 'center',
                                                height: '100%',
                                            }}
                                        >
                                            <CircularProgress sx={{ m: '-40px auto 0' }} />
                                        </Div>
                                    }
                                >
                                    <AppRoutes />
                                </Suspense>
                            </AppLayout>
                        </SnackbarProvider>
                    </JumboDialogProvider>
                </JumboRTL>
            </JumboTheme>
        </JumboApp>
    );
}

// Main App component that sets up providers
function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <Provider store={store}>
                <BrowserRouter history={history}>
                    <AppProvider>
                        <JumboAuthProvider>
                            <AppContent />
                        </JumboAuthProvider>
                    </AppProvider>
                </BrowserRouter>
            </Provider>
        </QueryClientProvider>
    );
}

export default App;
